import {
  CONDITION_TREE_AGE_WINDOW_SPAN_HOURS,
  EMPTY_STRING,
  MINUTES_PER_DAY
} from "src/components/constant/constant";
import type { CardAutocompleteOption } from "src/components/UI/cardAutocomplete";
import type { WalletAutocompleteOption } from "src/components/UI/walletAutocomplete";
import type { User } from "src/types/commonTypes";

// ─── Wire shape — mirrors conditionTreeSchema in
// @spendable-tech/workflow-primitives: a group node at the root (ALL/ANY/NONE
// over leaves and nested groups), group nesting capped at
// CONDITION_TREE_MAX_DEPTH levels with the root counting as 1. Stored on the
// workflow row as the `condition_tree` AWSJSON string.

export const CONDITION_TREE_GROUP_TYPES = ["ALL", "ANY", "NONE"] as const;
export type ConditionTreeGroupType = (typeof CONDITION_TREE_GROUP_TYPES)[number];

export const CONDITION_TREE_MAX_DEPTH = 5;

export const CONDITION_TREE_OPS = ["eq", "neq", "in", "not_in", "gte", "lte", "contains", "exists"] as const;
export type ConditionTreeOp = (typeof CONDITION_TREE_OPS)[number];

export type ConditionTreePrimitive = string | number | boolean;

export interface ConditionTreeLeafShape {
  field: string;
  op: ConditionTreeOp;
  value: ConditionTreePrimitive | ConditionTreePrimitive[];
}

export interface ConditionTreeGroupShape {
  type: ConditionTreeGroupType;
  conditions: (ConditionTreeLeafShape | ConditionTreeGroupShape)[];
}

export type ConditionTreeNodeShape = ConditionTreeLeafShape | ConditionTreeGroupShape;

export const isConditionTreeGroupShape = (node: ConditionTreeNodeShape): node is ConditionTreeGroupShape =>
  typeof node === "object" && node !== null && "conditions" in node;

const isPrimitive = (value: unknown): value is ConditionTreePrimitive =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean";

const isLeafShape = (value: unknown): value is ConditionTreeLeafShape => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;

  if (typeof v.field !== "string" || !(CONDITION_TREE_OPS as readonly string[]).includes(v.op as string)) {
    return false;
  }
  return Array.isArray(v.value) ? v.value.every(isPrimitive) : isPrimitive(v.value);
};

const isGroupShape = (value: unknown, depth: number): value is ConditionTreeGroupShape => {
  if (depth > CONDITION_TREE_MAX_DEPTH) return false;
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;

  if (!(CONDITION_TREE_GROUP_TYPES as readonly string[]).includes(v.type as string)) return false;
  if (!Array.isArray(v.conditions) || v.conditions.length === 0) return false;
  return v.conditions.every((node) => isLeafShape(node) || isGroupShape(node, depth + 1));
};

// Structural guard for stored trees (detail page, list summary) — accepts any
// schema-valid tree, not just the vocabularies this builder authors.
export const asConditionTreeShape = (value: unknown): ConditionTreeGroupShape | null =>
  isGroupShape(value, 1) ? value : null;

// ─── Availability + per-entity leaf vocabularies. FE mirror of the backend
// registry (registerConditionTreeAvailability in workflow-primitives). Every
// field is a TOP-LEVEL key of the snapshot its engine evaluates against —
// NOTIFICATION#TRANSACTION's nested transaction.* paths are the one exception
// (that producer only nests). Labels live in the workflowSubsystems label layer.

export const conditionTreeVocabularyKey = (subsystem?: string | null, entityType?: string | null): string =>
  `${subsystem ?? EMPTY_STRING}#${entityType ?? EMPTY_STRING}`;

export const TREE_KEY_TRANSITION_ACCOUNTING = "TRANSITION#ACCOUNTING";
export const TREE_KEY_NOTIFICATION_ACCOUNTING = "NOTIFICATION#ACCOUNTING";
export const TREE_KEY_NOTIFICATION_REIMBURSEMENT = "NOTIFICATION#REIMBURSEMENT";
export const TREE_KEY_NOTIFICATION_TRANSACTION = "NOTIFICATION#TRANSACTION";
export const TREE_KEY_NOTIFICATION_WALLET = "NOTIFICATION#WALLET";
export const TREE_KEY_SCHEDULER_ACCOUNTING = "SCHEDULER#ACCOUNTING";
export const TREE_KEY_SCHEDULER_CARD = "SCHEDULER#CARD";
export const TREE_KEY_SCHEDULER_CARD_SETTING = "SCHEDULER#CARD_SETTING";
export const TREE_KEY_DEFAULTS_ALLOCATION = "DEFAULTS#ALLOCATION";

export type ConditionTreeValueKind =
  | "enum"
  | "money"
  | "count"
  | "boolean"
  | "wallets"
  | "users"
  | "cards"
  | "text"
  | "codes";

export interface ConditionTreeFieldDef {
  field: string;
  valueKind: ConditionTreeValueKind;
  ops: readonly ConditionTreeOp[];
  // money: the snapshot's unit. The form always takes dollars; CENTS fields
  // are converted ×100 on the wire (same contract as legacy min_amount).
  unit?: "DOLLARS" | "CENTS";
}

// Leaves a dedicated form control owns instead of builder rows — the
// scheduler When inputs and the TRANSACTION time-of-day pair.
export type ConditionTreeAnchor = "AGE_WINDOW" | "EXPIRY_WITHIN" | "TIME_WINDOW";

export interface ConditionTreeVocabulary {
  key: string;
  fields: readonly ConditionTreeFieldDef[];
  // The trigger-seeded root leaf: field + check fixed, never removable.
  lockedField?: string;
  anchor?: ConditionTreeAnchor;
  // The rule may save with no conditions at all.
  allowEmpty?: boolean;
}

export const WALLET_TREE_FIELD_OP = "op";
export const WALLET_TREE_FIELD_AMOUNT = "amount";
export const WALLET_TREE_FIELD_BALANCE_AFTER = "balance_after_txn";
export const WALLET_TREE_FIELD_BALANCE_BEFORE = "balance_before_txn";
export const WALLET_TREE_FIELD_WALLET_ID = "wallet_id";
export const WALLET_TREE_FIELD_WALLET_TYPE = "wallet_type";
export const TREE_FIELD_TO_STATUS = "to_status";
export const TREE_FIELD_AGE_HOURS = "age_hours";
export const TREE_FIELD_DAYS_UNTIL_EXPIRY = "days_until_expiry";
export const TREE_FIELD_MINUTE_OF_DAY = "minute_of_day";

const MEMBERSHIP_OPS = ["in", "not_in"] as const;
const BOUND_OPS = ["gte", "lte"] as const;
const TEXT_OPS = ["contains", "eq", "neq"] as const;
const FLAG_OPS = ["eq"] as const;

const VOCABULARIES: readonly ConditionTreeVocabulary[] = [
  {
    // transaction-service workflowMatcher buildAccountingTreeSnapshot.
    // accounting_status is left out: the matcher pins it to from_state first.
    key: TREE_KEY_TRANSITION_ACCOUNTING,
    allowEmpty: true,
    fields: [
      { field: "activity_name", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "wallet_id", valueKind: "wallets", ops: MEMBERSHIP_OPS },
      { field: "amount", valueKind: "money", ops: BOUND_OPS, unit: "CENTS" },
      { field: "receipt_count", valueKind: "count", ops: BOUND_OPS },
      { field: "has_receipt", valueKind: "boolean", ops: FLAG_OPS },
      { field: "has_note", valueKind: "boolean", ops: FLAG_OPS },
      { field: "has_approvers", valueKind: "boolean", ops: FLAG_OPS },
      { field: "has_verifiers", valueKind: "boolean", ops: FLAG_OPS }
    ]
  },
  {
    // transaction-service accountingAudit + enrichAccountingSnapshot (+ op,
    // envelope from_status via SPD-488). No amount on this snapshot.
    key: TREE_KEY_NOTIFICATION_ACCOUNTING,
    lockedField: "op",
    fields: [
      { field: "op", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "from_status", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "status", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "wallet_id", valueKind: "wallets", ops: MEMBERSHIP_OPS },
      { field: "transacted_by", valueKind: "users", ops: MEMBERSHIP_OPS }
    ]
  },
  {
    // wallet-service reimbursementAudit — the Reimbursement row (+ op,
    // envelope from_status via SPD-488). amount is the claim total in dollars.
    key: TREE_KEY_NOTIFICATION_REIMBURSEMENT,
    lockedField: "op",
    fields: [
      { field: "op", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "from_status", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "status", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "amount", valueKind: "money", ops: BOUND_OPS, unit: "DOLLARS" },
      { field: "source_wallet_id", valueKind: "wallets", ops: MEMBERSHIP_OPS },
      { field: "destination_wallet_id", valueKind: "wallets", ops: MEMBERSHIP_OPS },
      { field: "requested_by", valueKind: "users", ops: MEMBERSHIP_OPS },
      { field: "merchant_name", valueKind: "text", ops: TEXT_OPS }
    ]
  },
  {
    // transaction-service cardAuthAudit — Novatti card transaction row with
    // the raw payload nested under transaction.* (raw dollars), plus the
    // SPD-488 top-level fields notify-service adds. to_status is the SPD-267
    // outcome pin workflow-service requires at the ALL root; minute_of_day is
    // owned by the time-of-day pair (TIME_WINDOW).
    key: TREE_KEY_NOTIFICATION_TRANSACTION,
    lockedField: TREE_FIELD_TO_STATUS,
    anchor: "TIME_WINDOW",
    fields: [
      { field: TREE_FIELD_TO_STATUS, valueKind: "enum", ops: ["eq", "in"] },
      { field: "decline_reason", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "mcc_category", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "day_of_week", valueKind: "enum", ops: ["in"] },
      { field: "transaction.amount", valueKind: "money", ops: BOUND_OPS, unit: "DOLLARS" },
      { field: "transaction.balance_after_txn", valueKind: "money", ops: BOUND_OPS, unit: "DOLLARS" },
      { field: "transaction.merchantName", valueKind: "text", ops: TEXT_OPS },
      // notify-service normalises Novatti's number-or-string code to a string.
      { field: "mcc", valueKind: "codes", ops: MEMBERSHIP_OPS },
      { field: "transaction.wallet_id", valueKind: "wallets", ops: MEMBERSHIP_OPS },
      { field: "user_id", valueKind: "users", ops: MEMBERSHIP_OPS },
      { field: "transaction_user_id", valueKind: "users", ops: MEMBERSHIP_OPS }
    ]
  },
  {
    // wallet-service walletBalanceAudit — flat keys (+ op). Raw dollars.
    key: TREE_KEY_NOTIFICATION_WALLET,
    lockedField: WALLET_TREE_FIELD_OP,
    fields: [
      { field: WALLET_TREE_FIELD_OP, valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: WALLET_TREE_FIELD_AMOUNT, valueKind: "money", ops: BOUND_OPS, unit: "DOLLARS" },
      { field: WALLET_TREE_FIELD_BALANCE_AFTER, valueKind: "money", ops: BOUND_OPS, unit: "DOLLARS" },
      { field: WALLET_TREE_FIELD_BALANCE_BEFORE, valueKind: "money", ops: BOUND_OPS, unit: "DOLLARS" },
      { field: WALLET_TREE_FIELD_WALLET_ID, valueKind: "wallets", ops: ["in"] },
      { field: WALLET_TREE_FIELD_WALLET_TYPE, valueKind: "enum", ops: ["in"] }
    ]
  },
  {
    // report-service snapshot.ts buildAccountingEvaluationSnapshot. The age
    // window is the When anchor, not a builder row.
    key: TREE_KEY_SCHEDULER_ACCOUNTING,
    anchor: "AGE_WINDOW",
    allowEmpty: true,
    fields: [
      { field: "status", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "wallet_id", valueKind: "wallets", ops: MEMBERSHIP_OPS },
      { field: "user_id", valueKind: "users", ops: MEMBERSHIP_OPS },
      { field: "merchant_name", valueKind: "text", ops: TEXT_OPS },
      { field: "amount", valueKind: "money", ops: BOUND_OPS, unit: "CENTS" },
      { field: "receipt_count", valueKind: "count", ops: BOUND_OPS },
      { field: "has_receipt", valueKind: "boolean", ops: FLAG_OPS }
    ]
  },
  {
    // report-service cardHandler buildCardSnapshot + days_until_expiry (the
    // When anchor).
    key: TREE_KEY_SCHEDULER_CARD,
    anchor: "EXPIRY_WITHIN",
    allowEmpty: true,
    fields: [
      { field: "card_status", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "card_type", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "user_id", valueKind: "users", ops: MEMBERSHIP_OPS }
    ]
  },
  {
    // report-service cardSettingHandler buildCardSettingEvaluationSnapshot.
    // No conditions = every active card connection (SPD-467).
    key: TREE_KEY_SCHEDULER_CARD_SETTING,
    allowEmpty: true,
    fields: [
      { field: "wallet_id", valueKind: "wallets", ops: MEMBERSHIP_OPS },
      { field: "card_id", valueKind: "cards", ops: MEMBERSHIP_OPS },
      { field: "wallet_type", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "card_type", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "card_status", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "card_setting_status", valueKind: "enum", ops: MEMBERSHIP_OPS }
    ]
  },
  {
    // transaction-service allocationDefaultsRunner — AllocationSnapshot as-is.
    // No conditions = split every activity in the scope wallets (the runner
    // reads an absent condition as {}).
    key: TREE_KEY_DEFAULTS_ALLOCATION,
    allowEmpty: true,
    fields: [
      { field: "merchant_name", valueKind: "text", ops: TEXT_OPS },
      { field: "mcc", valueKind: "codes", ops: MEMBERSHIP_OPS },
      { field: "transaction_type_key", valueKind: "enum", ops: MEMBERSHIP_OPS },
      { field: "cardholder_user_id", valueKind: "users", ops: MEMBERSHIP_OPS }
    ]
  }
];

const VOCABULARY_BY_KEY = new Map(VOCABULARIES.map((v) => [v.key, v]));

export const getConditionTreeVocabulary = (key: string): ConditionTreeVocabulary | undefined =>
  VOCABULARY_BY_KEY.get(key);

export const isConditionTreeAvailable = (subsystem?: string | null, entityType?: string | null): boolean =>
  VOCABULARY_BY_KEY.has(conditionTreeVocabularyKey(subsystem, entityType));

export const conditionTreeFieldDef = (key: string, field: string): ConditionTreeFieldDef | undefined =>
  VOCABULARY_BY_KEY.get(key)?.fields.find((def) => def.field === field);

// ─── Form (in-memory) model. One value slot per kind so switching the field
// never destroys a half-typed value; ids are render keys only and never reach
// the wire.

export interface ConditionTreeLeafForm {
  kind: "leaf";
  id: string;
  field: string;
  op: ConditionTreeOp | "";
  // enum picks
  values: string[];
  // money / count / text / codes (comma-separated) input
  text: string;
  // boolean pick
  flag: "" | "true" | "false";
  wallets: WalletAutocompleteOption[];
  users: User[];
  cards: CardAutocompleteOption[];
  // The trigger-seeded root leaf: field and check stay fixed and the row
  // cannot be removed — the same guard the pre-tree trigger fields enforced.
  locked?: boolean;
}

export interface ConditionTreeGroupForm {
  kind: "group";
  id: string;
  type: ConditionTreeGroupType;
  children: (ConditionTreeLeafForm | ConditionTreeGroupForm)[];
}

export type ConditionTreeNodeForm = ConditionTreeLeafForm | ConditionTreeGroupForm;

let treeNodeSeq = 0;
const nextTreeNodeId = (): string => `ctn-${++treeNodeSeq}`;

export const createTreeLeaf = (partial?: Partial<Omit<ConditionTreeLeafForm, "kind" | "id">>): ConditionTreeLeafForm => ({
  kind: "leaf",
  id: nextTreeNodeId(),
  field: EMPTY_STRING,
  op: EMPTY_STRING,
  values: [],
  text: EMPTY_STRING,
  flag: EMPTY_STRING,
  wallets: [],
  users: [],
  cards: [],
  ...partial
});

const TEXT_VALUE_KINDS: readonly ConditionTreeValueKind[] = ["money", "count", "text", "codes"];

// Switching a row's field keeps only what the new field can hold: enum picks
// it offers, and typed text within the same kind of input. Booleans default
// to their first check.
export const retargetTreeLeaf = (
  key: string,
  leaf: ConditionTreeLeafForm,
  field: string,
  enumOptions: readonly string[]
): ConditionTreeLeafForm => {
  const prevKind = conditionTreeFieldDef(key, leaf.field)?.valueKind;
  const nextDef = conditionTreeFieldDef(key, field);
  const nextKind = nextDef?.valueKind;
  const textKindChanged =
    prevKind !== undefined &&
    nextKind !== undefined &&
    prevKind !== nextKind &&
    TEXT_VALUE_KINDS.includes(prevKind) &&
    TEXT_VALUE_KINDS.includes(nextKind);

  return {
    ...leaf,
    field,
    op: nextDef?.ops[0] ?? EMPTY_STRING,
    values: nextKind === "enum" ? leaf.values.filter((v) => enumOptions.includes(v)) : leaf.values,
    text: textKindChanged ? EMPTY_STRING : leaf.text,
    flag: nextKind === "boolean" && !leaf.flag ? "true" : leaf.flag
  };
};

export const createTreeGroup = (type: ConditionTreeGroupType): ConditionTreeGroupForm => ({
  kind: "group",
  id: nextTreeNodeId(),
  type,
  children: [createTreeLeaf()]
});

export const createEmptyTreeRoot = (): ConditionTreeGroupForm => ({
  kind: "group",
  id: nextTreeNodeId(),
  type: "ALL",
  children: []
});

export interface ConditionTreeSeed {
  ops?: readonly string[];
  outcome?: string;
}

// Fresh-create seed: an ALL root, holding the locked trigger leaf when the
// vocabulary has one — the trigger's ops (WALLET / ACCOUNTING / REIMBURSEMENT)
// or its pinned outcome (TRANSACTION).
export const createConditionTreeSeed = (key: string, seed: ConditionTreeSeed = {}): ConditionTreeGroupForm => {
  const root = createEmptyTreeRoot();
  const lockedField = getConditionTreeVocabulary(key)?.lockedField;
  const outcome = seed.outcome ? [seed.outcome] : [];
  const values = lockedField === TREE_FIELD_TO_STATUS ? outcome : [...(seed.ops ?? [])];

  if (lockedField && values.length) {
    // The outcome pin is a single eq (template twins); op sets stay membership.
    const op = lockedField === TREE_FIELD_TO_STATUS ? "eq" : "in";

    root.children.push(createTreeLeaf({ field: lockedField, op, values, locked: true }));
  }
  return root;
};

// ─── Scheduler anchors. The friendly When inputs become root-level leaves:
// - AGE_WINDOW (Once): age_hours in [min, min + 23] — floored hours, so each
//   record sits in the window on exactly one daily run; daily only.
// - AGE_WINDOW (Repeat): age_hours >= min only — re-fires at the rule's
//   frequency while the record matches.
// - EXPIRY_WITHIN: days_until_expiry in [1, N] — the flat 0 < d <= N match.


export interface ConditionTreeAnchorValues {
  age_min_hours?: number;
  // Repeat: the age lower bound only, no upper bound.
  age_open_ended?: boolean;
  expires_within_days?: number;
  // TIME_WINDOW, Sydney minutes of the day: [from, to) — to is exclusive.
  minute_from?: number;
  minute_to?: number;
}


// "21:30" → 1290; undefined for anything that isn't HH:mm.
export const hhmmToMinuteOfDay = (raw: string): number | undefined => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(raw.trim());

  return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
};

export const minuteOfDayToHhmm = (minute: number): string => {
  const wrapped = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`;
};

// The HH:mm time-of-day pair → TIME_WINDOW anchor values (both or neither).
export const timeWindowToAnchor = (start: string, end: string): ConditionTreeAnchorValues => {
  const from = hhmmToMinuteOfDay(start);
  const to = hhmmToMinuteOfDay(end);

  return from !== undefined && to !== undefined && from !== to ? { minute_from: from, minute_to: to } : {};
};

const minuteLeaf = (op: "gte" | "lte", value: number): ConditionTreeLeafShape => ({
  field: TREE_FIELD_MINUTE_OF_DAY,
  op,
  value
});

export const buildConditionTreeAnchorLeaves = (
  anchor: ConditionTreeAnchor | undefined,
  values: ConditionTreeAnchorValues
): ConditionTreeNodeShape[] => {
  if (anchor === "TIME_WINDOW") {
    const { minute_from: from, minute_to: to } = values;

    if (typeof from !== "number" || typeof to !== "number" || from === to) return [];
    const bounds = [minuteLeaf("gte", from), minuteLeaf("lte", to - 1)];

    // Overnight (from > to) wraps past midnight: either side matches.
    return from < to ? bounds : [{ type: "ANY", conditions: bounds }];
  }
  if (anchor === "AGE_WINDOW" && typeof values.age_min_hours === "number") {
    const lowerBound: ConditionTreeLeafShape = { field: TREE_FIELD_AGE_HOURS, op: "gte", value: values.age_min_hours };

    if (values.age_open_ended) return [lowerBound];
    return [
      lowerBound,
      { field: TREE_FIELD_AGE_HOURS, op: "lte", value: values.age_min_hours + CONDITION_TREE_AGE_WINDOW_SPAN_HOURS }
    ];
  }
  if (anchor === "EXPIRY_WITHIN" && typeof values.expires_within_days === "number") {
    return [
      { field: TREE_FIELD_DAYS_UNTIL_EXPIRY, op: "gte", value: 1 },
      { field: TREE_FIELD_DAYS_UNTIL_EXPIRY, op: "lte", value: values.expires_within_days }
    ];
  }
  return [];
};

const ANCHOR_FIELDS: Record<ConditionTreeAnchor, readonly string[]> = {
  AGE_WINDOW: [TREE_FIELD_AGE_HOURS, "age_days"],
  EXPIRY_WITHIN: [TREE_FIELD_DAYS_UNTIL_EXPIRY],
  TIME_WINDOW: [TREE_FIELD_MINUTE_OF_DAY]
};

const numericLeaf = (node: ConditionTreeNodeShape, field: string, op: ConditionTreeOp): number | undefined =>
  !isConditionTreeGroupShape(node) && node.field === field && node.op === op && typeof node.value === "number"
    ? node.value
    : undefined;

const treeMentionsField = (group: ConditionTreeGroupShape, fields: readonly string[]): boolean =>
  group.conditions.some((node) =>
    isConditionTreeGroupShape(node) ? treeMentionsField(node, fields) : fields.includes(node.field)
  );

// Tree scheduler rules testing record age are daily-only.
export const conditionTreeHasAgeHours = (tree: ConditionTreeGroupShape): boolean =>
  treeMentionsField(tree, [TREE_FIELD_AGE_HOURS]);

const isMinute = (n: number | undefined, low: number): n is number =>
  typeof n === "number" && Number.isInteger(n) && n >= low && n < MINUTES_PER_DAY;

// The optional time-of-day window: a root gte/lte pair (from <= to-1) or one
// root ANY group holding exactly [gte from, lte to-1] (overnight).
const splitTimeWindow = (
  tree: ConditionTreeGroupShape
): { values: ConditionTreeAnchorValues; rest: ConditionTreeNodeShape[] } | null => {
  const fields = ANCHOR_FIELDS.TIME_WINDOW;

  if (!treeMentionsField(tree, fields)) return { values: {}, rest: tree.conditions };
  if (tree.type !== "ALL") return null;
  const window = (start: number, lastMinute: number, rest: ConditionTreeNodeShape[]) =>
    isMinute(start, 0) && isMinute(lastMinute, -1) && !(rest.length && treeMentionsField({ type: "ALL", conditions: rest }, fields))
      ? { values: { minute_from: start, minute_to: lastMinute + 1 }, rest }
      : null;
  const gteIndex = tree.conditions.findIndex((node) => numericLeaf(node, TREE_FIELD_MINUTE_OF_DAY, "gte") !== undefined);
  const lteIndex = tree.conditions.findIndex((node) => numericLeaf(node, TREE_FIELD_MINUTE_OF_DAY, "lte") !== undefined);

  if (gteIndex !== -1 && lteIndex !== -1) {
    const start = numericLeaf(tree.conditions[gteIndex], TREE_FIELD_MINUTE_OF_DAY, "gte") as number;
    const lastMinute = numericLeaf(tree.conditions[lteIndex], TREE_FIELD_MINUTE_OF_DAY, "lte") as number;
    const others = tree.conditions.filter((_, i) => i !== gteIndex && i !== lteIndex);

    return start <= lastMinute ? window(start, lastMinute, others) : null;
  }
  const groupIndex = tree.conditions.findIndex(
    (node) =>
      isConditionTreeGroupShape(node) &&
      node.type === "ANY" &&
      node.conditions.length === 2 &&
      node.conditions.some((child) => numericLeaf(child, TREE_FIELD_MINUTE_OF_DAY, "gte") !== undefined) &&
      node.conditions.some((child) => numericLeaf(child, TREE_FIELD_MINUTE_OF_DAY, "lte") !== undefined)
  );

  if (groupIndex === -1) return null;
  const group = tree.conditions[groupIndex] as ConditionTreeGroupShape;
  const from = group.conditions.map((c) => numericLeaf(c, TREE_FIELD_MINUTE_OF_DAY, "gte")).find((n) => n !== undefined);
  const last = group.conditions.map((c) => numericLeaf(c, TREE_FIELD_MINUTE_OF_DAY, "lte")).find((n) => n !== undefined);

  if (from === undefined || last === undefined || from <= last + 1) return null;
  return window(from, last, tree.conditions.filter((_, i) => i !== groupIndex));
};

// Splits the anchor off an ALL root. Null when the tree tests the anchor
// field in any other shape — the builder can't represent that.
export const splitConditionTreeAnchor = (
  anchor: ConditionTreeAnchor,
  tree: ConditionTreeGroupShape
): { values: ConditionTreeAnchorValues; rest: ConditionTreeNodeShape[] } | null => {
  if (anchor === "TIME_WINDOW") return splitTimeWindow(tree);
  if (tree.type !== "ALL") return null;
  const field = anchor === "AGE_WINDOW" ? TREE_FIELD_AGE_HOURS : TREE_FIELD_DAYS_UNTIL_EXPIRY;
  const gteIndex = tree.conditions.findIndex((node) => numericLeaf(node, field, "gte") !== undefined);
  const lteIndex = tree.conditions.findIndex((node) => numericLeaf(node, field, "lte") !== undefined);

  if (anchor === "AGE_WINDOW" && gteIndex !== -1 && lteIndex === -1) {
    const startAge = numericLeaf(tree.conditions[gteIndex], field, "gte") as number;
    const others = tree.conditions.filter((_, i) => i !== gteIndex);

    if (!Number.isInteger(startAge) || startAge < 0) return null;
    if (others.length && treeMentionsField({ type: "ALL", conditions: others }, ANCHOR_FIELDS[anchor])) return null;
    return { values: { age_min_hours: startAge, age_open_ended: true }, rest: others };
  }
  if (gteIndex === -1 || lteIndex === -1) return null;
  const low = numericLeaf(tree.conditions[gteIndex], field, "gte") as number;
  const high = numericLeaf(tree.conditions[lteIndex], field, "lte") as number;
  const rest = tree.conditions.filter((_, i) => i !== gteIndex && i !== lteIndex);

  if (rest.length && treeMentionsField({ type: "ALL", conditions: rest }, ANCHOR_FIELDS[anchor])) return null;
  if (anchor === "AGE_WINDOW") {
    if (!Number.isInteger(low) || low < 0 || high !== low + CONDITION_TREE_AGE_WINDOW_SPAN_HOURS) return null;
    return { values: { age_min_hours: low }, rest };
  }
  if (low !== 1 || !Number.isInteger(high) || high < 1) return null;
  return { values: { expires_within_days: high }, rest };
};

// ─── Serialize: form → wire. Incomplete leaves and empty groups are pruned;
// an empty root serializes to undefined, meaning the rule sends no
// condition_tree at all (workflow-service accepts absence).

const parseCodes = (raw: string): string[] =>
  raw
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);

const toWireMoney = (num: number, unit: ConditionTreeFieldDef["unit"]): number =>
  unit === "CENTS" ? Math.round(num * 100) : num;

const serializeLeaf = (key: string, leaf: ConditionTreeLeafForm): ConditionTreeLeafShape | null => {
  const def = conditionTreeFieldDef(key, leaf.field);

  if (!def || !leaf.op || !def.ops.includes(leaf.op)) return null;
  const { field, op } = leaf;

  switch (def.valueKind) {
    case "enum":
      if (!leaf.values.length) return null;
      return op === "eq" || op === "neq" ? { field, op, value: leaf.values[0] } : { field, op, value: [...leaf.values] };
    case "money": {
      const raw = leaf.text.trim();

      if (raw === EMPTY_STRING) return null;
      const num = Number(raw);

      if (Number.isNaN(num) || num < 0) return null;
      return { field, op, value: toWireMoney(num, def.unit) };
    }
    case "count": {
      const raw = leaf.text.trim();
      const num = Number(raw);

      if (raw === EMPTY_STRING || !Number.isInteger(num) || num < 0) return null;
      return { field, op, value: num };
    }
    case "boolean":
      return leaf.flag ? { field, op, value: leaf.flag === "true" } : null;
    case "wallets": {
      const ids = leaf.wallets.map((w) => w.wallet_id).filter(Boolean);

      return ids.length ? { field, op, value: ids } : null;
    }
    case "users": {
      const ids = leaf.users.map((u) => u.id).filter(Boolean);

      return ids.length ? { field, op, value: ids } : null;
    }
    case "cards": {
      const ids = leaf.cards.map((c) => c.card_id).filter(Boolean);

      return ids.length ? { field, op, value: ids } : null;
    }
    case "text": {
      const raw = leaf.text.trim();

      return raw ? { field, op, value: raw } : null;
    }
    case "codes": {
      const codes = parseCodes(leaf.text);

      return codes.length ? { field, op, value: codes } : null;
    }
  }
};

const serializeGroup = (key: string, group: ConditionTreeGroupForm): ConditionTreeGroupShape | null => {
  const conditions = group.children
    .map((child) => (child.kind === "group" ? serializeGroup(key, child) : serializeLeaf(key, child)))
    .filter((node): node is ConditionTreeNodeShape => node !== null);

  return conditions.length ? { type: group.type, conditions } : null;
};

const isPinnedLeaf = (node: ConditionTreeNodeForm): node is ConditionTreeLeafForm =>
  node.kind === "leaf" && Boolean(node.locked);

// The trigger pin and the anchors always AND, so an any/none root nests under
// an ALL wrapper: ALL[pin, anchors, ANY[rows]].
const serializeJoinedRoot = (
  key: string,
  root: ConditionTreeGroupForm,
  anchorNodes: ConditionTreeNodeShape[]
): ConditionTreeGroupShape | undefined => {
  const pins = root.children
    .filter(isPinnedLeaf)
    .map((leaf) => serializeLeaf(key, leaf))
    .filter((node): node is ConditionTreeLeafShape => node !== null);
  const joined = serializeGroup(key, { ...root, children: root.children.filter((child) => !isPinnedLeaf(child)) });
  const fixed = [...pins, ...anchorNodes];

  if (!fixed.length) return joined ?? undefined;
  return { type: "ALL", conditions: joined ? [...fixed, joined] : fixed };
};

// Anchor nodes lead the ALL root, right after the locked trigger leaf.
export const serializeConditionTree = (
  key: string,
  root: ConditionTreeGroupForm,
  anchorNodes: ConditionTreeNodeShape[] = []
): ConditionTreeGroupShape | undefined => {
  if (root.type !== "ALL") return serializeJoinedRoot(key, root, anchorNodes);
  const body = serializeGroup(key, root)?.conditions ?? [];
  const lockedField = getConditionTreeVocabulary(key)?.lockedField;
  const lead = body[0] && !isConditionTreeGroupShape(body[0]) && body[0].field === lockedField ? 1 : 0;
  const conditions = [...body.slice(0, lead), ...anchorNodes, ...body.slice(lead)];

  return conditions.length ? { type: root.type, conditions } : undefined;
};

// ─── Hydrate: wire → form. Strict over what the builder can author — the
// vocabulary's field/op/value shapes and ONE level of grouping (root → sets
// → condition rows, any join at either level). Anything else (unknown leaves,
// group-in-group trees stored via the API) returns null, and the caller
// preserves the stored tree verbatim instead of lossily editing it. The wire
// schema's deeper depth-5 cap is untouched — this is a UI limit only.

// Groups may sit at root (1) or one level below it (2).
const UI_MAX_GROUP_DEPTH = 2;

const toStringArray = (value: ConditionTreePrimitive | ConditionTreePrimitive[]): string[] | null => {
  if (!Array.isArray(value)) return null;
  return value.every((v) => typeof v === "string") ? ([...value] as string[]) : null;
};

const userStub = (id: string): User => ({
  id,
  displayId: EMPTY_STRING,
  cognitoUserName: EMPTY_STRING,
  firstName: EMPTY_STRING,
  lastName: EMPTY_STRING,
  fullName: id
});

const fromWireMoney = (value: number, unit: ConditionTreeFieldDef["unit"]): string =>
  String(unit === "CENTS" ? value / 100 : value);

const hydrateLeaf = (key: string, leaf: ConditionTreeLeafShape): ConditionTreeLeafForm | null => {
  const def = conditionTreeFieldDef(key, leaf.field);

  if (!def || !def.ops.includes(leaf.op)) return null;
  const { field, op, value } = leaf;

  switch (def.valueKind) {
    case "enum": {
      const values = typeof value === "string" && (op === "eq" || op === "neq") ? [value] : toStringArray(value);

      return values && values.length ? createTreeLeaf({ field, op, values }) : null;
    }
    case "money":
      return typeof value === "number" && value >= 0
        ? createTreeLeaf({ field, op, text: fromWireMoney(value, def.unit) })
        : null;
    case "count":
      return typeof value === "number" && Number.isInteger(value) && value >= 0
        ? createTreeLeaf({ field, op, text: String(value) })
        : null;
    case "boolean":
      return typeof value === "boolean" ? createTreeLeaf({ field, op, flag: value ? "true" : "false" }) : null;
    case "wallets": {
      const ids = toStringArray(value);

      // Id-named stubs — WalletAutocomplete fetches display names from the ids.
      return ids && ids.length
        ? createTreeLeaf({ field, op, wallets: ids.map((id) => ({ wallet_id: id, name: id })) })
        : null;
    }
    case "users": {
      const ids = toStringArray(value);

      return ids && ids.length ? createTreeLeaf({ field, op, users: ids.map(userStub) }) : null;
    }
    case "cards": {
      const ids = toStringArray(value);

      return ids && ids.length ? createTreeLeaf({ field, op, cards: ids.map((id) => ({ card_id: id })) }) : null;
    }
    case "text":
      return typeof value === "string" && value.trim() ? createTreeLeaf({ field, op, text: value }) : null;
    case "codes": {
      const codes = toStringArray(value);

      return codes && codes.length && codes.every((c) => c.trim())
        ? createTreeLeaf({ field, op, text: codes.join(", ") })
        : null;
    }
  }
};

const hydrateGroup = (
  key: string,
  type: ConditionTreeGroupType,
  conditions: ConditionTreeNodeShape[],
  depth: number
): ConditionTreeGroupForm | null => {
  if (depth > UI_MAX_GROUP_DEPTH) return null;
  const children: ConditionTreeNodeForm[] = [];

  for (const node of conditions) {
    const child = isConditionTreeGroupShape(node)
      ? hydrateGroup(key, node.type, node.conditions, depth + 1)
      : hydrateLeaf(key, node);

    if (!child) return null;
    children.push(child);
  }
  return { kind: "group", id: nextTreeNodeId(), type, children };
};

export interface HydratedConditionTree {
  root: ConditionTreeGroupForm;
  anchor: ConditionTreeAnchorValues;
}

const isPinLeafShape = (node: ConditionTreeNodeShape, lockedField?: string): node is ConditionTreeLeafShape =>
  !isConditionTreeGroupShape(node) && node.field === lockedField && (node.op === "in" || node.op === "eq");

const canonicalJson = (value: unknown): string =>
  JSON.stringify(value, (_key, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, (v as Record<string, unknown>)[k]]))
      : v
  );

// The serialized any/none root (ALL[pin, anchors, ANY|NONE[rows]]) reopens as
// that join. Only taken when it re-serializes to the stored tree exactly.
const hydrateJoinedRoot = (
  key: string,
  vocabulary: ConditionTreeVocabulary,
  wire: ConditionTreeGroupShape,
  conditions: ConditionTreeNodeShape[],
  anchor: ConditionTreeAnchorValues
): ConditionTreeGroupForm | null => {
  if (wire.type !== "ALL") return null;
  const pinIndex = conditions.findIndex((node) => isPinLeafShape(node, vocabulary.lockedField));
  const anchorLeaves = buildConditionTreeAnchorLeaves(vocabulary.anchor, anchor);

  if (pinIndex === -1 && !anchorLeaves.length) return null;
  const others = conditions.filter((_, i) => i !== pinIndex);
  const [set] = others;

  if (others.length !== 1 || !isConditionTreeGroupShape(set) || set.type === "ALL") return null;
  const pin = pinIndex === -1 ? null : hydrateLeaf(key, conditions[pinIndex] as ConditionTreeLeafShape);

  if (pinIndex !== -1 && !pin) return null;
  const root = hydrateGroup(key, set.type, set.conditions, 1);

  if (!root) return null;
  if (pin) root.children.unshift({ ...pin, locked: true });
  return canonicalJson(serializeConditionTree(key, root, anchorLeaves)) === canonicalJson(wire) ? root : null;
};

export const hydrateConditionTree = (key: string, value: unknown): HydratedConditionTree | null => {
  const vocabulary = getConditionTreeVocabulary(key);
  const wire = asConditionTreeShape(value);

  if (!vocabulary || !wire) return null;
  let conditions = wire.conditions;
  let anchor: ConditionTreeAnchorValues = {};

  if (vocabulary.anchor) {
    const split = splitConditionTreeAnchor(vocabulary.anchor, wire);

    if (!split) return null;
    conditions = split.rest;
    anchor = split.values;
  }
  const joinedRoot = hydrateJoinedRoot(key, vocabulary, wire, conditions, anchor);

  if (joinedRoot) return { root: joinedRoot, anchor };
  const root = conditions.length ? hydrateGroup(key, wire.type, conditions, 1) : createEmptyTreeRoot();

  if (!root) return null;
  // Re-lock the trigger-seeded leaf so edit mirrors create: the row stays
  // non-removable with its membership-only check. Only an ALL root pins it.
  if (vocabulary.lockedField && wire.type === "ALL") {
    const seeded = root.children.find(
      (child): child is ConditionTreeLeafForm =>
        child.kind === "leaf" && child.field === vocabulary.lockedField && (child.op === "in" || child.op === "eq")
    );

    if (seeded) seeded.locked = true;
  }
  return { root, anchor };
};

// ─── Validation. Submit requires every leaf complete (and at least one
// condition unless the vocabulary allows none); the builder uses
// treeLeafError for row-level marking.

export type ConditionTreeLeafProblem = "incomplete" | "negative" | "whole";

export const treeLeafError = (key: string, leaf: ConditionTreeLeafForm): ConditionTreeLeafProblem | undefined => {
  const def = conditionTreeFieldDef(key, leaf.field);

  if (!def || !leaf.op) return "incomplete";
  switch (def.valueKind) {
    case "enum":
      return leaf.values.length ? undefined : "incomplete";
    case "money": {
      const raw = leaf.text.trim();

      if (raw === EMPTY_STRING) return "incomplete";
      const num = Number(raw);

      return Number.isNaN(num) || num < 0 ? "negative" : undefined;
    }
    case "count": {
      const raw = leaf.text.trim();

      if (raw === EMPTY_STRING) return "incomplete";
      const num = Number(raw);

      return Number.isInteger(num) && num >= 0 ? undefined : "whole";
    }
    case "boolean":
      return leaf.flag ? undefined : "incomplete";
    case "wallets":
      return leaf.wallets.length ? undefined : "incomplete";
    case "users":
      return leaf.users.length ? undefined : "incomplete";
    case "cards":
      return leaf.cards.length ? undefined : "incomplete";
    case "text":
      return leaf.text.trim() ? undefined : "incomplete";
    case "codes":
      return parseCodes(leaf.text).length ? undefined : "incomplete";
  }
};

export const collectTreeLeaves = (group: ConditionTreeGroupForm): ConditionTreeLeafForm[] =>
  group.children.flatMap((child) => (child.kind === "group" ? collectTreeLeaves(child) : [child]));

const CONDITION_TREE_EMPTY_SET_ERROR = "Every set needs at least one condition.";

export const isEmptyTreeSet = (node: ConditionTreeNodeForm): boolean =>
  node.kind === "group" && node.children.length === 0;

const hasEmptyTreeSet = (group: ConditionTreeGroupForm): boolean =>
  group.children.some((child) => child.kind === "group" && (isEmptyTreeSet(child) || hasEmptyTreeSet(child)));

export const validateConditionTree = (key: string, root: ConditionTreeGroupForm): string | undefined => {
  const leaves = collectTreeLeaves(root);

  if (hasEmptyTreeSet(root)) return CONDITION_TREE_EMPTY_SET_ERROR;
  if (leaves.length === 0) {
    return getConditionTreeVocabulary(key)?.allowEmpty ? undefined : "Add at least one condition for this rule to check.";
  }
  const problems = leaves.map((leaf) => treeLeafError(key, leaf));

  if (problems.includes("negative")) return "Amounts must be 0 or higher.";
  if (problems.includes("whole")) return "Counts must be whole numbers, 0 or higher.";
  if (problems.includes("incomplete")) {
    return "Finish every condition — each row needs a condition, a check and a value.";
  }
  return undefined;
};

// ─── Shared read helpers.

const rootMembershipLeaf = (tree: ConditionTreeGroupShape, field: string): ConditionTreeLeafShape | undefined => {
  if (tree.type !== "ALL") return undefined;
  return tree.conditions.find(
    (node): node is ConditionTreeLeafShape =>
      !isConditionTreeGroupShape(node) &&
      node.field === field &&
      ((node.op === "in" && Array.isArray(node.value)) || (node.op === "eq" && typeof node.value === "string"))
  );
};

const leafStrings = (leaf: ConditionTreeLeafShape): string[] | null =>
  typeof leaf.value === "string" ? [leaf.value] : toStringArray(leaf.value);

// The trigger-pinning values: the root-level locked leaf the seed plants.
// Feeds findTriggerForStoredRule so tree rules hydrate back to the same
// trigger the flat `ops` / `to_status` condition did.
export const extractTreeTriggerShape = (tree: ConditionTreeGroupShape): { ops: string[]; to_status: string[] } => {
  const opsLeaf = rootMembershipLeaf(tree, WALLET_TREE_FIELD_OP);
  const statusLeaf = rootMembershipLeaf(tree, TREE_FIELD_TO_STATUS);

  return {
    ops: (opsLeaf && leafStrings(opsLeaf)) ?? [],
    to_status: (statusLeaf && leafStrings(statusLeaf)) ?? []
  };
};

// Splits the trigger-seeded leaf off the root so preview sentences can phrase
// it as the trigger ("has a deposit or transfer in", "is declined") and the
// rest as the if-clause.
export const splitRootLockedLeaf = (
  tree: ConditionTreeGroupShape,
  field: string
): { values: string[] | null; rest: ConditionTreeGroupShape | null } => {
  const leaf = rootMembershipLeaf(tree, field);

  if (!leaf) return { values: null, rest: tree };
  const remaining = tree.conditions.filter((node) => node !== leaf);

  return {
    values: leafStrings(leaf),
    rest: remaining.length ? { type: "ALL", conditions: remaining } : null
  };
};

const walletFieldsOf = (key?: string): string[] => {
  const vocabulary = key ? getConditionTreeVocabulary(key) : undefined;

  return vocabulary
    ? vocabulary.fields.filter((def) => def.valueKind === "wallets").map((def) => def.field)
    : [WALLET_TREE_FIELD_WALLET_ID];
};

// Every wallet id referenced by wallet leaves — the detail page resolves
// these to display names before rendering the sentence.
export const collectConditionTreeWalletIds = (tree: ConditionTreeGroupShape, key?: string): string[] => {
  const fields = walletFieldsOf(key);
  const ids = new Set<string>();
  const walk = (group: ConditionTreeGroupShape) => {
    group.conditions.forEach((node) => {
      if (isConditionTreeGroupShape(node)) {
        walk(node);
        return;
      }
      if (fields.includes(node.field) && Array.isArray(node.value)) {
        node.value.forEach((v) => {
          if (typeof v === "string" && v) ids.add(v);
        });
      }
    });
  };

  walk(tree);
  return Array.from(ids);
};

// Wallet display names held in form state (picked or hydrated options) — the
// live preview names wallets without a cache lookup.
export const collectTreeWalletNames = (root: ConditionTreeGroupForm): Map<string, string> => {
  const names = new Map<string, string>();

  collectTreeLeaves(root).forEach((leaf) =>
    leaf.wallets.forEach((w) => {
      if (w.wallet_id && w.name && w.name !== w.wallet_id) names.set(w.wallet_id, w.name);
    })
  );
  return names;
};
